import { UserResource } from '@clerk/types'

import isEmpty from 'lodash/fp/isEmpty'
import isEqual from 'lodash/fp/isEqual'
import isFunction from 'lodash/fp/isFunction'
import isNil from 'lodash/fp/isNil'
import merge from 'lodash/fp/merge'
import pick from 'lodash/fp/pick'

import type { CoreUser } from '../types'

import endpointsDefinition from '../api/endpoints'
import { toast } from '../components/Toaster'
import { Plans } from '../enums'
import { getRoleFlags } from './authentication'
import { type Logger, createLogger } from './log'

export function fullName<
  T extends {
    email?: string | null
    firstName?: string | null
    lastName?: string | null
  },
>(user: T | null | undefined, { fallbackToEmail = true } = {}) {
  return (
    (`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() ||
      (fallbackToEmail ? user?.email : '')) ??
    ''
  )
}

export function phoneNumber<T extends CoreUser>(user: T | null | undefined) {
  if (isEmpty(user?.phone?.number)) {
    return undefined
  }

  return `+${user?.phone?.countryCode || '33'}${user?.phone?.number}`.trim()
}

export function hasActiveMaxSubscription<T extends CoreUser>(
  user: T | null | undefined,
  searchParams?: URLSearchParams,
) {
  let searchParamsToUse = searchParams
  const { isActuallyAdmin, isAdmin, isCoach } = getRoleFlags(user, searchParams)
  if (!isActuallyAdmin) {
    searchParamsToUse = undefined
  }

  if (searchParamsToUse?.has('hasMax')) {
    return searchParamsToUse?.get('hasMax') !== 'false'
  }

  const asRole = searchParamsToUse?.get('asRole')
  if (!isNil(asRole)) {
    return asRole === 'max'
  }

  return (
    isAdmin || isCoach || (user?.subscriptionPlan?.startsWith('max_') ?? false)
  )
}

export function hasActiveMediaSubscription<T extends CoreUser>(
  user: T | null | undefined,
  searchParams?: URLSearchParams,
) {
  let searchParamsToUse = searchParams
  const { isActuallyAdmin, isStaff } = getRoleFlags(user, searchParams)
  if (!isActuallyAdmin) {
    searchParamsToUse = undefined
  }

  if (searchParamsToUse?.has('hasPlus')) {
    return searchParamsToUse?.get('hasPlus') !== 'false'
  }

  const asRole = searchParamsToUse?.get('asRole')
  if (!isNil(asRole)) {
    return asRole === 'plus' || asRole === 'max'
  }

  return (
    isStaff ||
    hasActiveMaxSubscription(user, searchParams) ||
    (user?.subscriptionPlan?.startsWith('plus_') ?? false)
  )
}

async function patchUser<T extends CoreUser>(
  currentUser: T,
  updates: Partial<T>,
  endpoints: ReturnType<typeof endpointsDefinition>,
  clerkUser?: UserResource | null | undefined,
  customLogger: Logger | undefined = undefined,
) {
  const actualLogger = isNil(customLogger)
    ? createLogger('patchUser')
    : customLogger

  actualLogger.setContext?.({
    ...actualLogger.getContext?.(),
    updates,
  })

  const user = currentUser

  actualLogger.setContext?.({
    ...actualLogger.getContext?.(),
    updates,
    userToUpdate: user,
  })

  if (isEmpty(updates)) {
    actualLogger.debug(`Nothing to update in the user, skipping...`)

    return null
  }

  if (!isEmpty(clerkUser)) {
    const clerkUpdates = pick(['firstName', 'lastName'], updates)

    if (!isEmpty(clerkUpdates)) {
      // Fire & Forget the Clerk user update. It's not that important anyway.
      clerkUser.update(clerkUpdates).catch((error) => {
        actualLogger.warn(`Unable to update the Clerk user`, {}, error)
      })
    }
  }

  if (isEmpty(user)) {
    actualLogger.error(`Current user is empty! Are they connected?`)

    throw new Error('User is empty')
  }

  let newUser = currentUser
  if (isEqual(user, merge(user, updates))) {
    actualLogger.info(`User is already up to date!`)
  } else {
    const updatedUser = await endpoints.users.patch(updates)
    newUser = updatedUser
  }

  return isEqual(currentUser, newUser) ? null : newUser
}

async function updateUser<T extends CoreUser>(
  currentUser: T | null | undefined,
  updates: Partial<T>,
  endpoints: ReturnType<typeof endpointsDefinition>,
  clerkUser: UserResource | null | undefined = undefined,
  customLogger: Logger | undefined = undefined,
) {
  const actualLogger = isNil(customLogger)
    ? createLogger('updateUser')
    : customLogger

  actualLogger.setContext?.({
    ...actualLogger.getContext?.(),
    updates,
  })

  if (isEmpty(currentUser)) {
    actualLogger.error(`Current user is empty! Are they connected?`)

    return currentUser
  }

  let updatedUser: T | null = null
  try {
    updatedUser = await patchUser<T>(
      currentUser,
      updates,
      endpoints,
      clerkUser,
      actualLogger,
    )
  } catch (error) {
    const actualError = error as Error
    actualLogger.error(
      `Unable to patch the user: "${actualError.message}"`,
      {},
      actualError,
    )

    try {
      toast.error('toast.error.user.update.unexpected')
    } catch {
      // Nothing to do here.
    }

    return false
  }

  return updatedUser
}

type UpdateUserAndContextParams<T extends CoreUser> = {
  endpoints: ReturnType<typeof endpointsDefinition>
  user: T | null
  clerkUser?: UserResource | null | undefined
  logger?: Logger
  setUser?: (user: T | null) => void
  updates?: Partial<T>
}
export async function updateUserAndContext<T extends CoreUser>({
  clerkUser = undefined,
  endpoints,
  logger,
  setUser,
  updates = {},
  user,
}: UpdateUserAndContextParams<T>) {
  const updatedUser = await updateUser(
    user,
    updates,
    endpoints,
    clerkUser,
    logger,
  )

  if (updatedUser !== false && !isEmpty(updatedUser) && isFunction(setUser)) {
    setUser(updatedUser)
  }

  return updatedUser
}

export function isUserConnected(
  user: CoreUser | null | undefined,
  searchParams?: URLSearchParams,
) {
  if (isEmpty(user?.uuid)) {
    return false
  }

  let searchParamsToUse = searchParams
  const { isActuallyAdmin } = getRoleFlags(user, searchParams)
  if (!isActuallyAdmin) {
    searchParamsToUse = undefined
  }

  if (searchParamsToUse?.has('asRole')) {
    return searchParamsToUse?.get('asRole') !== 'guest'
  }

  return true
}

export function isUserPaying(
  user: CoreUser | null | undefined,
  searchParams?: URLSearchParams,
) {
  return (
    isUserConnected(user, searchParams) &&
    hasActiveMediaSubscription(user, searchParams)
  )
}

export function isUserSubscriptionScheduledToUpdate(
  user: CoreUser | null | undefined,
  searchParams?: URLSearchParams,
) {
  let searchParamsToUse = searchParams
  const { isActuallyAdmin } = getRoleFlags(user, searchParams)
  if (!isActuallyAdmin) {
    searchParamsToUse = undefined
  }

  return (
    user?.subscriptionScheduled || searchParamsToUse?.has('withScheduledUpdate')
  )
}

export function getUserPlan(
  user: CoreUser | null | undefined,
  searchParams?: URLSearchParams,
) {
  let searchParamsToUse = searchParams
  const { isActuallyAdmin } = getRoleFlags(user, searchParams)
  if (!isActuallyAdmin) {
    searchParamsToUse = undefined
  }

  if (searchParamsToUse?.has('asRole')) {
    switch (searchParamsToUse.get('asRole')) {
      case 'max':
        return Plans.Max

      case 'plus':
        return Plans.Plus

      default:
        return Plans.Free
    }
  }

  return user?.subscriptionPlan ?? Plans.Free
}

export function isUserMax(
  user: CoreUser | null | undefined,
  searchParams?: URLSearchParams,
) {
  return hasActiveMaxSubscription(user, searchParams)
}

export function isUserPlus(
  user: CoreUser | null | undefined,
  searchParams?: URLSearchParams,
) {
  return getUserPlan(user, searchParams).startsWith('plus_')
}

export function isUserFree(
  user: CoreUser | null | undefined,
  searchParams?: URLSearchParams,
) {
  return getUserPlan(user, searchParams) === Plans.Free
}

export function isSuperAdmin(user: CoreUser | null | undefined) {
  const { isActuallyAdmin } = getRoleFlags(user)

  return (
    isActuallyAdmin &&
    ['clement@snowball.xyz', 'emeline@snowball.xyz'].includes(user?.email ?? '')
  )
}
